// Searchable select for static, in-memory option lists (e.g. the ISO country
// list). Same look-and-feel as RelationshipPicker (type-to-filter dropdown,
// selected value as a clearable chip) but synchronous — options are filtered
// locally, no searchFn/query round-trip — and keyed by string value instead of
// a numeric FK id.

import { useEffect, useId, useRef, useState } from 'react';

export interface SearchSelectOption {
  value: string;
  label: string;
  /** Optional secondary line under the label (e.g. the code). */
  sub?: string | null;
}

interface SearchSelectProps {
  label: string;
  value: string | null;
  onChange: (value: string | null) => void;
  options: ReadonlyArray<SearchSelectOption>;
  placeholder?: string;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  emptyLabel?: string;
}

export function SearchSelect({
  label,
  value,
  onChange,
  options,
  placeholder = 'Type to search…',
  hint,
  required,
  disabled,
  emptyLabel = 'No matches.',
}: SearchSelectProps) {
  const reactId = useId();
  const inputId = `ss-${reactId}`;
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  // Click-outside to close the dropdown.
  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const selected = value != null ? options.find((o) => o.value === value) ?? null : null;
  const q = query.trim().toLowerCase();
  const filtered =
    q === ''
      ? options
      : options.filter(
          (o) =>
            o.label.toLowerCase().includes(q) ||
            // `sub` is the secondary line (country code, vendor email…). It is
            // on screen, so operators reasonably expect to be able to type it.
            (o.sub ?? '').toLowerCase().includes(q) ||
            // Match `value` only when it is NOT a bare numeric id. For the
            // country list `value` is the meaningful code ('ma'), but for a
            // record picker it is a surrogate FK — matching it there turns a
            // search for "12" into a hit on every id containing 12.
            (!/^\d+$/.test(o.value) && o.value.toLowerCase().includes(q)),
        );

  return (
    <div ref={wrapperRef} className="relative flex flex-col gap-1">
      <label
        htmlFor={inputId}
        className="text-[11px] font-medium uppercase tracking-wide text-[color:var(--bo-mute)]"
      >
        {label}
        {required ? <span className="ms-0.5 text-[#9f1239]">*</span> : null}
      </label>

      {selected ? (
        <div className="flex items-center justify-between gap-2 rounded-md border border-[color:var(--bo-line)] bg-[color:var(--bo-bg)] px-3 py-2">
          <div className="min-w-0">
            <div className="truncate text-[13px] font-medium text-[color:var(--bo-ink)]">
              {selected.label}
            </div>
            {selected.sub ? (
              <div className="truncate text-[11px] text-[color:var(--bo-mute)]">{selected.sub}</div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => onChange(null)}
            disabled={disabled}
            className="text-[12px] font-medium text-[#9f1239] hover:underline disabled:opacity-50"
          >
            Clear
          </button>
        </div>
      ) : (
        <input
          id={inputId}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          className="h-10 w-full rounded-md border border-[color:var(--bo-line)] bg-white px-3 text-[13px] text-[color:var(--bo-ink)] focus:border-[color:var(--bo-mute)] focus:outline-none disabled:cursor-not-allowed disabled:bg-[color:var(--bo-bg)]"
        />
      )}

      {hint ? <span className="text-[11px] text-[color:var(--bo-mute)]">{hint}</span> : null}

      {open && !selected ? (
        <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-72 overflow-y-auto rounded-md border border-[color:var(--bo-line)] bg-white shadow-lg">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-[12px] text-[color:var(--bo-mute)]">{emptyLabel}</div>
          ) : (
            <ul className="divide-y divide-[color:var(--bo-line)]">
              {filtered.map((opt) => (
                <li key={opt.value}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(opt.value);
                      setQuery('');
                      setOpen(false);
                    }}
                    className="block w-full px-3 py-2 text-left text-[13px] text-[color:var(--bo-ink)] hover:bg-[color:var(--bo-bg)]"
                  >
                    <div className="truncate font-medium">{opt.label}</div>
                    {opt.sub ? (
                      <div className="truncate text-[11px] text-[color:var(--bo-mute)]">
                        {opt.sub}
                      </div>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
